import { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useSetAtom } from "jotai";
import { MondayBoardSelector } from "frontend/canvas-designer-new/monday-import-items/monday-board-selector/monday-board-selector";
import { useMondayImportItems } from "frontend/canvas-designer-new/monday-import-items/monday-import.hook";
import { ResourceType } from "frontend/canvas-designer-new/monday-import-items/monday-import-items.types";
import { GanttChartSkeleton } from "./gantt-chart-skeleton";
import { GanttChartMappingSkeleton } from "./gantt-chart-mapping-skeleton";
import { MappingSection } from "./mapping-section";
import { useBoardValue } from "frontend/state/board-state";
import ConfirmButton from "../../components/confirm-button";
import SubSubMenu from "../../components/sub-sub-menu";
import { useSyncedGanttChart, ganttBoardFilter } from "./use-synced-gantt-chart";
import { GanttItemsSelection } from "./gantt-items-selection";
import { GanttStep, type GanttChartGroup } from "./synced-gantt-chart.types";
import styles from "./synced-gantt-chart.module.css";
import Divider from "../../components/divider";
import SyncedWidgetHeader from "../../components/synced-widget-header";
import Checkbox from "frontend/ui-components/design-system/checkbox/checkbox";
import { MondayIntegrationType } from "shared/integrations/integration";
import { placeGanttElement } from "shared/datamodel/gantt";
import { placeMondayTask } from "shared/datamodel/monday-task";
import consts from "shared/consts";
import { TasksColors } from "elements/gantt/constants";
import { getMetadataMappingForGantt } from "frontend/canvas-designer-new/integrations/convert-to-monday/logic/gantt-mapping-hooks/use-mapping-settings-gantt-cards";
import { GanttConnector } from "shared/datamodel/schemas/gantt";
import { errorMessageAtom } from "state-atoms";
import { useTemplateAutoPlacement } from "frontend/hooks/place-template-hooks";
import { centerElementsAroundOrigin } from "utils/template-utils";

export default function SyncedGanttChart({
  onComplete,
  onHideBackButton,
}: {
  onComplete: (data: unknown) => void;
  onHideBackButton?: (hide: boolean) => void;
}) {
  const [{ board }] = useBoardValue();
  const [step, setStep] = useState<GanttStep>("mapping");

  useEffect(() => {
    onHideBackButton?.(step === "items");
  }, [step, onHideBackButton]);

  const {
    workspaces,
    workspacesLoading,
    workspacesInitialLoading,
    loadMoreWorkspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    boards,
    boardsLoading,
    boardsInitialLoading,
    selectedBoard,
    setSelectedBoard,
    loadMoreBoards,
    items: groupedItems,
    itemsLoading,
    hasMoreItems,
    loadMoreItems,
  } = useMondayImportItems({
    withColumns: true,
    withColumnValues: true,
    withSubItemColumns: true,
    boardCompatibilityFilter: ganttBoardFilter,
  });

  const {
    mondayIntegration,
    addBoardIntegration,
    columns,
    subItemsColumns,
    columnMapping,
    handleItemMappingChange,
    handleGlobalDependencyToggle,
    dependencyEnabled,
    isNextDisabled,
    getColumnsForField,
    isColumnEnabled,
    hasDependencyColumns,
    itemsUsesDateColumns,
    subItemsUsesDateColumns,
  } = useSyncedGanttChart({
    documentId: board?.documentId,
    selectedBoard,
  });

  const [isCreating, setIsCreating] = useState(false);

  const setErrorMessage = useSetAtom(errorMessageAtom);
  const autoPlaceTemplate = useTemplateAutoPlacement();

  const itemGroups: GanttChartGroup[] = useMemo(() => {
    return groupedItems.map((group) => ({
      id: group.id,
      name: group.name,
      color: group.color,
      items: group.items.map((item) => ({
        id: item.id,
        name: item.name,
        subItems: item.subItems?.map((sub) => ({ id: sub.id, name: sub.name })),
      })),
    }));
  }, [groupedItems]);

  const handleBoardSelect = useCallback(
    (pickedBoard: ResourceType) => {
      const fullBoard = boards.find((b) => b.id === pickedBoard.id);
      setSelectedBoard(fullBoard ?? null);
    },
    [boards, setSelectedBoard]
  );

  const handleNext = useCallback(() => {
    if (!isNextDisabled) {
      setStep("items");
    }
  }, [isNextDisabled]);

  const handleBack = useCallback(() => {
    setStep("mapping");
  }, []);

  const handleImport = useCallback(
    async (selectedItemIds: string[]) => {
      if (!selectedBoard || !mondayIntegration || isCreating) return;

      setIsCreating(true);

      try {
        const configuration = {
          boardId: selectedBoard.id,
          type: MondayIntegrationType.tasks,
          columns: columns.map((c) => ({ id: c.id, type: c.type })),
          columnMapping: {
            timeline: columnMapping.items.timeline,
            startDate: columnMapping.items.startDate,
            endDate: columnMapping.items.endDate,
            dependency: columnMapping.items.dependencyEnabled ? columnMapping.items.dependency : null,
          },
        };

        const integrationId = await addBoardIntegration(mondayIntegration.id, configuration);

        // Check if selectedBoard has subitem_board info and create integration for it
        const subitemBoard = (selectedBoard as { subitem_board?: { id: string } }).subitem_board;
        let subItemsIntegrationId: string | undefined;
        if (subitemBoard) {
          const subItemsConfiguration = {
            boardId: subitemBoard.id,
            type: MondayIntegrationType.tasks,
            columns:
              subItemsColumns.length > 0
                ? subItemsColumns.map((c) => ({ id: c.id, type: c.type }))
                : columns.map((c) => ({ id: c.id, type: c.type })),
            columnMapping: {
              timeline: columnMapping.subItems.timeline,
              startDate: columnMapping.subItems.startDate,
              endDate: columnMapping.subItems.endDate,
              dependency: columnMapping.subItems.dependencyEnabled ? columnMapping.subItems.dependency : null,
            },
            isSubitemsBoard: true,
            parentBoardId: selectedBoard.id,
          };
          // Create board integration for sub-items board
          subItemsIntegrationId = await addBoardIntegration(mondayIntegration.id, subItemsConfiguration);
        }

        const selectedItemsWithGroups = groupedItems
          .map((group) => ({
            group: { id: group.id, name: group.name, color: group.color },
            items: group.items.filter((item) => {
              // Include item if it's selected OR if it has selected sub-items
              const isItemSelected = selectedItemIds.includes(item.id);
              const hasSelectedSubItems =
                item.subItems && item.subItems.some((subItem) => selectedItemIds.includes(subItem.id));
              return isItemSelected || hasSelectedSubItems;
            }),
          }))
          .filter((g) => g.items.length > 0);

        const selectedItems = selectedItemsWithGroups.flatMap((g) => g.items);

        const parseDateFromColumnValue = (
          columnValues: Array<{ id: string; value?: string; type: string }> | undefined,
          columnId: string | null,
          columnType: "timeline" | "date",
          dateType: "from" | "to"
        ): number | null => {
          if (!columnId || !columnValues) return null;
          const colValue = columnValues.find((cv) => cv.id === columnId);
          if (!colValue?.value) return null;
          try {
            const parsed = JSON.parse(colValue.value);
            if (columnType === "timeline") {
              return dateType === "from" ? new Date(parsed.from).getTime() : new Date(parsed.to).getTime();
            } else {
              // date column
              return new Date(parsed.date).getTime();
            }
          } catch {
            return null;
          }
        };

        const getDependencies = (
          columnValues: Array<{ id: string; value?: string; type: string; linked_item_ids?: string[] }> | undefined,
          depColumnId: string | null
        ): string[] => {
          if (!depColumnId || !columnValues) return [];
          const depValue = columnValues.find((cv) => cv.id === depColumnId);
          if (!depValue) return [];

          // Use linked_item_ids directly if available (from Monday API)
          if (depValue.linked_item_ids && Array.isArray(depValue.linked_item_ids)) {
            return depValue.linked_item_ids.map((id) => id.toString());
          }

          // Fallback to parsing value JSON (legacy format)
          if (!depValue.value) return [];
          try {
            const parsed = JSON.parse(depValue.value);
            return (parsed?.linkedPulseIds ?? []).map((d: { linkedPulseId: number }) => d.linkedPulseId.toString());
          } catch {
            return [];
          }
        };

        const useTimeline = !!columnMapping.items.timeline;
        const dateColumnId = useTimeline ? columnMapping.items.timeline : columnMapping.items.startDate;
        const endDateColumnId = useTimeline ? columnMapping.items.timeline : columnMapping.items.endDate;
        const dependencyColumnId = columnMapping.items.dependencyEnabled ? columnMapping.items.dependency : null;

        const useSubItemsTimeline = !!columnMapping.subItems.timeline;
        const subItemsDateColumnId = useSubItemsTimeline
          ? columnMapping.subItems.timeline
          : columnMapping.subItems.startDate;
        const subItemsEndDateColumnId = useSubItemsTimeline
          ? columnMapping.subItems.timeline
          : columnMapping.subItems.endDate;
        const subItemsDependencyColumnId = columnMapping.subItems.dependencyEnabled
          ? columnMapping.subItems.dependency
          : null;

        let ganttStartDate = Number.MAX_SAFE_INTEGER;
        let ganttEndDate = Number.MIN_SAFE_INTEGER;

        const { id: ganttId, item: ganttItem } = placeGanttElement({ x: 0, y: 0 }, Date.now(), Date.now());
        const ganttElementId = `${consts.CANVAS_ELEMENTS.GANTT}-${ganttId}`;

        const elements: { [key: string]: unknown } = {};
        const nodeIdMap = new Map<string, string>();
        const subItemElements: Array<{
          element: { fieldValues?: Record<string, unknown> };
          fromDate: number | null;
          toDate: number | null;
          rowId: string;
        }> = [];

        const itemsWithoutDates: string[] = [];

        const ganttRows: { id: string; title: string; color: string }[] = [];
        const groupIdToRowId = new Map<string, string>();

        let groupIndex = 0;
        for (const groupData of selectedItemsWithGroups) {
          const rowId = groupIndex.toString();
          const rowColor = TasksColors[groupIndex % TasksColors.length];

          ganttRows.push({
            id: rowId,
            title: groupData.group.name,
            color: rowColor,
          });
          groupIdToRowId.set(groupData.group.id, rowId);
          groupIndex++;
        }

        for (const groupData of selectedItemsWithGroups) {
          const rowId = groupIdToRowId.get(groupData.group.id) ?? "0";
          const rowColor = ganttRows.find((r) => r.id === rowId)?.color ?? TasksColors[0];

          for (const item of groupData.items) {
            // Only process main item if it's selected
            const isMainItemSelected = selectedItemIds.includes(item.id);

            if (isMainItemSelected) {
              // Process main item
              const fromDate = parseDateFromColumnValue(
                item.column_values,
                dateColumnId,
                useTimeline ? "timeline" : "date",
                "from"
              );
              // If not using timeline and endDate is not mapped, use startDate as endDate
              const toDate =
                useTimeline || endDateColumnId
                  ? parseDateFromColumnValue(
                      item.column_values,
                      endDateColumnId,
                      useTimeline ? "timeline" : "date",
                      "to"
                    )
                  : fromDate; // Use startDate as endDate when endDate is not mapped

              // Skip items without start date value
              if (!fromDate) {
                itemsWithoutDates.push(item.name);
                continue; // Skip creating element for this item
              }

              // If using timeline or endDate is mapped, require both dates
              if ((useTimeline || endDateColumnId) && !toDate) {
                itemsWithoutDates.push(item.name);
                continue; // Skip creating element for this item
              }

              // Use startDate as endDate if endDate is not available
              const finalToDate = toDate ?? fromDate;

              // Update Gantt date range
              if (fromDate < ganttStartDate) ganttStartDate = fromDate;
              if (finalToDate > ganttEndDate) ganttEndDate = finalToDate;

              const mondayTaskItem = placeMondayTask(
                { x: 0, y: 0 },
                {
                  mondayItemId: item.id,
                  integrationId: integrationId,
                  fill: rowColor,
                  baseOnElement: {
                    fieldValues: {
                      groupByColumn: rowId,
                      title: item.name,
                      fromDate: fromDate,
                      toDate: finalToDate,
                    },
                  },
                }
              );

              mondayTaskItem.mondayTask.containerId = ganttElementId;
              mondayTaskItem.mondayTask.fieldValues = {
                groupByColumn: rowId,
                title: item.name,
                fromDate: fromDate,
                toDate: finalToDate,
              };

              const elementId = `${consts.CANVAS_ELEMENTS.MONDAY_TASK}-${mondayTaskItem.id}`;
              elements[elementId] = mondayTaskItem.mondayTask;
              nodeIdMap.set(item.id, elementId);
            }

            // Process sub-items if they exist and are selected
            // Use sub-items integration if available, otherwise fall back to main integration
            if (item.subItems && item.subItems.length > 0 && integrationId) {
              const integrationIdForSubItems = subItemsIntegrationId ?? integrationId;
              for (const subItem of item.subItems) {
                // Only process sub-items that are in the selected items list
                if (!selectedItemIds.includes(subItem.id)) {
                  continue;
                }

                // Sub-items might not have column_values if they weren't fetched with column values
                // Try to parse dates from column_values if available
                const subItemFromDate = subItem.column_values
                  ? parseDateFromColumnValue(
                      subItem.column_values,
                      subItemsDateColumnId,
                      useSubItemsTimeline ? "timeline" : "date",
                      "from"
                    )
                  : null;
                // If not using timeline and endDate is not mapped, use startDate as endDate
                const subItemToDate =
                  useSubItemsTimeline || subItemsEndDateColumnId
                    ? subItem.column_values
                      ? parseDateFromColumnValue(
                          subItem.column_values,
                          subItemsEndDateColumnId,
                          useSubItemsTimeline ? "timeline" : "date",
                          "to"
                        )
                      : null
                    : subItemFromDate; // Use startDate as endDate when endDate is not mapped

                // Skip sub-items without start date value
                if (!subItemFromDate) {
                  itemsWithoutDates.push(subItem.name);
                  continue; // Skip creating element for this sub-item
                }

                // If using timeline or endDate is mapped, require both dates
                if ((useSubItemsTimeline || subItemsEndDateColumnId) && !subItemToDate) {
                  itemsWithoutDates.push(subItem.name);
                  continue; // Skip creating element for this sub-item
                }

                // Use startDate as endDate if endDate is not available
                const finalSubItemToDate = subItemToDate ?? subItemFromDate;

                // Update Gantt date range to include sub-item dates
                if (subItemFromDate < ganttStartDate) ganttStartDate = subItemFromDate;
                if (finalSubItemToDate > ganttEndDate) ganttEndDate = finalSubItemToDate;

                const subItemMondayTaskItem = placeMondayTask(
                  { x: 0, y: 0 },
                  {
                    mondayItemId: subItem.id,
                    integrationId: integrationIdForSubItems,
                    fill: rowColor,
                    baseOnElement: {
                      fieldValues: {
                        groupByColumn: rowId,
                        title: subItem.name,
                        fromDate: subItemFromDate,
                        toDate: finalSubItemToDate,
                      },
                    },
                  }
                );

                subItemMondayTaskItem.mondayTask.containerId = ganttElementId;

                // Set initial fieldValues (will be clamped to final Gantt range later)
                subItemMondayTaskItem.mondayTask.fieldValues = {
                  groupByColumn: rowId, // Use same row as parent
                  title: subItem.name,
                  fromDate: subItemFromDate,
                  toDate: finalSubItemToDate,
                };

                // Store sub-item element and dates for later clamping to final Gantt range
                subItemElements.push({
                  element: subItemMondayTaskItem.mondayTask,
                  fromDate: subItemFromDate,
                  toDate: finalSubItemToDate,
                  rowId: rowId,
                });

                const subItemElementId = `${consts.CANVAS_ELEMENTS.MONDAY_TASK}-${subItemMondayTaskItem.id}`;
                elements[subItemElementId] = subItemMondayTaskItem.mondayTask;
                nodeIdMap.set(subItem.id, subItemElementId);
              }
            }
          }
        }

        // Create dependency connectors
        const connectors: GanttConnector[] = [];
        for (const item of selectedItems) {
          const toId = nodeIdMap.get(item.id);
          if (!toId) continue; // Skip if item wasn't created (shouldn't happen, but safety check)

          const dependencies = getDependencies(item.column_values, dependencyColumnId);

          for (const depId of dependencies) {
            const fromId = nodeIdMap.get(depId);
            if (fromId && toId) {
              connectors.push({
                id: `${item.id}-${depId}`,
                type: "monday",
                from: fromId,
                to: toId,
              });
            }
          }
        }

        // Process sub-items dependencies
        for (const groupData of selectedItemsWithGroups) {
          for (const item of groupData.items) {
            if (item.subItems && item.subItems.length > 0) {
              for (const subItem of item.subItems) {
                if (!selectedItemIds.includes(subItem.id)) continue;

                const subItemToId = nodeIdMap.get(subItem.id);
                if (!subItemToId) continue;

                const subItemDependencies = subItem.column_values
                  ? getDependencies(subItem.column_values, subItemsDependencyColumnId)
                  : [];

                for (const depId of subItemDependencies) {
                  const fromId = nodeIdMap.get(depId);
                  if (fromId && subItemToId) {
                    connectors.push({
                      id: `${subItem.id}-${depId}`,
                      type: "monday",
                      from: fromId,
                      to: subItemToId,
                    });
                  }
                }
              }
            }
          }
        }

        // If no valid dates were found, use default range
        if (ganttStartDate === Number.MAX_SAFE_INTEGER || ganttEndDate === Number.MIN_SAFE_INTEGER) {
          ganttStartDate = Date.now();
          ganttEndDate = Date.now() + 7 * 24 * 60 * 60 * 1000;
        }

        // Ensure we have a valid date range (end date must be after start date)
        if (ganttEndDate <= ganttStartDate) {
          ganttEndDate = ganttStartDate + 7 * 24 * 60 * 60 * 1000;
        }

        // Update sub-item dates and ensure they're within reasonable bounds
        // Use the calculated range as bounds, but allow some flexibility
        const rangePadding = 7 * 24 * 60 * 60 * 1000; // 7 days padding
        const minAllowedDate = ganttStartDate - rangePadding;
        const maxAllowedDate = ganttEndDate + rangePadding;

        for (const { element, fromDate, toDate, rowId: subItemRowId } of subItemElements) {
          // Use the current fieldValues dates if available, otherwise use the original parsed dates
          const currentFromDate = (element.fieldValues?.fromDate as number) ?? fromDate;
          const currentToDate = (element.fieldValues?.toDate as number) ?? toDate;

          // Clamp dates to be within reasonable bounds (with padding)
          const clampedFromDate = currentFromDate
            ? Math.max(minAllowedDate, Math.min(maxAllowedDate, currentFromDate))
            : ganttStartDate;
          const clampedToDate = currentToDate
            ? Math.max(minAllowedDate, Math.min(maxAllowedDate, currentToDate))
            : ganttEndDate;

          // Ensure toDate is after fromDate
          const finalToDate = Math.max(clampedFromDate, clampedToDate);

          // Preserve all existing fieldValues (especially groupByColumn for rowId)
          element.fieldValues = {
            ...element.fieldValues,
            groupByColumn: subItemRowId, // Ensure rowId is set
            title: element.fieldValues?.title ?? "",
            fromDate: clampedFromDate,
            toDate: finalToDate,
          };
        }

        // Recalculate Gantt range to include all final dates (main items + sub-items)
        let finalGanttStartDate = ganttStartDate;
        let finalGanttEndDate = ganttEndDate;

        // Check all main items' final dates
        for (const groupData of selectedItemsWithGroups) {
          for (const item of groupData.items) {
            const elementId = nodeIdMap.get(item.id);
            if (elementId) {
              const element = elements[elementId] as { fieldValues?: { fromDate?: number; toDate?: number } };
              const itemFromDate = element?.fieldValues?.fromDate;
              const itemToDate = element?.fieldValues?.toDate;
              if (itemFromDate && itemFromDate < finalGanttStartDate) {
                finalGanttStartDate = itemFromDate;
              }
              if (itemToDate && itemToDate > finalGanttEndDate) {
                finalGanttEndDate = itemToDate;
              }
            }
          }
        }

        // Check all sub-items' final dates
        for (const { element } of subItemElements) {
          const subItemFromDate = element.fieldValues?.fromDate as number | undefined;
          const subItemToDate = element.fieldValues?.toDate as number | undefined;
          if (subItemFromDate && subItemFromDate < finalGanttStartDate) {
            finalGanttStartDate = subItemFromDate;
          }
          if (subItemToDate && subItemToDate > finalGanttEndDate) {
            finalGanttEndDate = subItemToDate;
          }
        }

        // Ensure we have a valid date range
        if (finalGanttEndDate <= finalGanttStartDate) {
          finalGanttEndDate = finalGanttStartDate + 7 * 24 * 60 * 60 * 1000;
        }

        ganttItem.startDate = finalGanttStartDate;
        ganttItem.endDate = finalGanttEndDate;
        ganttItem.connectors = connectors;
        ganttItem.splits = [
          {
            id: "groupByColumn",
            type: "monday",
            rows: ganttRows,
          },
        ];

        const daysDiff = (ganttEndDate - ganttStartDate) / (1000 * 60 * 60 * 24);
        if (daysDiff > 180) {
          ganttItem.granularity = "quarter";
        } else if (daysDiff > 28) {
          ganttItem.granularity = "month";
        } else if (daysDiff > 21) {
          ganttItem.granularity = "week";
        } else {
          ganttItem.granularity = "day";
        }

        elements[ganttElementId] = ganttItem;

        const mapping = getMetadataMappingForGantt({
          "date-tab": useTimeline ? "timeline" : "start-end",
          timeline: useTimeline ? columnMapping.items.timeline ?? "" : "",
          start_date: useTimeline ? "" : columnMapping.items.startDate ?? "",
          end_date: useTimeline ? "" : columnMapping.items.endDate ?? "",
          dependency: dependencyColumnId ?? "",
        });

        const subItemsMapping = getMetadataMappingForGantt({
          "date-tab": useSubItemsTimeline ? "timeline" : "start-end",
          timeline: useSubItemsTimeline ? columnMapping.subItems.timeline ?? "" : "",
          start_date: useSubItemsTimeline ? "" : columnMapping.subItems.startDate ?? "",
          end_date: useSubItemsTimeline ? "" : columnMapping.subItems.endDate ?? "",
          dependency: subItemsDependencyColumnId ?? "",
        });

        const metadata: { [id: string]: unknown } = {
          [selectedBoard.id]: {
            type: "boardColumnsMapping",
            lastModifiedTimestamp: Date.now(),
            boardColumnsMapping: mapping,
          },
          [`IntgMapping-${selectedBoard.id}`]: {
            ...mapping,
          },
        };

        // Add metadata for sub-items board if it exists
        if (subitemBoard && subItemsIntegrationId) {
          metadata[subitemBoard.id] = {
            type: "boardColumnsMapping",
            lastModifiedTimestamp: Date.now(),
            isSubitemsBoard: true,
            boardColumnsMapping: subItemsMapping,
          };
          metadata[`IntgMapping-${subitemBoard.id}`] = {
            ...subItemsMapping,
          };
        }

        const centeredElements = centerElementsAroundOrigin(elements);
        const elementsObject = Object.fromEntries(Object.entries(centeredElements));

        // Convert elements to template format and use template auto-placement
        const template = {
          id: "gantt-chart-elements",
          data: {
            elements: elementsObject,
            metadata: metadata || {},
          },
        };

        await autoPlaceTemplate(template as any, "cim-widget-synced-gantt-chart");

        if (itemsWithoutDates.length > 0) {
          const count = itemsWithoutDates.length;
          const itemText = count === 1 ? "item" : "items";
          setErrorMessage(
            `${count} ${itemText} or sub-item${count === 1 ? "" : "s"} ${
              count === 1 ? "was" : "were"
            } not placed in Gantt Chart due to missing date values`
          );
        }

        onComplete(null);
      } catch (error) {
        console.error("Error creating gantt chart:", error);
      } finally {
        setIsCreating(false);
      }
    },
    [
      selectedBoard,
      mondayIntegration,
      isCreating,
      columns,
      subItemsColumns,
      columnMapping,
      addBoardIntegration,
      groupedItems,
      autoPlaceTemplate,
      onComplete,
      setErrorMessage,
    ]
  );

  if (workspacesInitialLoading) {
    return <GanttChartSkeleton />;
  }

  if (step === "items") {
    return (
      <SubSubMenu onGoBack={handleBack} subMenuType="gantt-chart-items-selection">
        <GanttItemsSelection
          groups={itemGroups}
          isLoading={itemsLoading}
          isCreating={isCreating}
          hasMoreItems={hasMoreItems}
          loadMoreItems={loadMoreItems}
          onImport={handleImport}
        />
      </SubSubMenu>
    );
  }

  const isBoardSelected = !!selectedBoard;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.selectorsRow}>
          <MondayBoardSelector
            workspaces={workspaces}
            selectedWorkspace={selectedWorkspace}
            setSelectedWorkspace={setSelectedWorkspace}
            workspacesLoading={workspacesLoading}
            workspacesInitialLoading={workspacesInitialLoading}
            loadMoreWorkspaces={loadMoreWorkspaces}
            boards={boards}
            selectedBoard={selectedBoard}
            setSelectedBoard={handleBoardSelect}
            boardsLoading={boardsLoading}
            boardsInitialLoading={boardsInitialLoading}
            loadMoreBoards={loadMoreBoards}
            workspacePickerTitle="Select workspace"
            boardPickerTitle="Select board"
            isBoardDisabled={(board) => {
              const fullBoard = boards.find((b) => b.id === board?.id);
              return fullBoard ? !fullBoard.isCompatible : false;
            }}
            disabledBoardTooltip="This board needs timeline or date columns"
            pickerWidth="158px"
            pickerStyle={{
              color: "#1E1E1E",
              fontSize: "10px",
              fontWeight: 400,
              marginBottom: "4px",
            }}
          />
        </div>

        <SyncedWidgetHeader title="Map out dates by column" subtitle="Select the relevant columns from the board" />
      </div>

      <Divider />

      {isBoardSelected ? (
        <div className={styles.mappingSection}>
          <MappingSection
            title="Items"
            target="items"
            columnMapping={columnMapping}
            columns={columns}
            getColumnsForField={getColumnsForField}
            isColumnEnabled={isColumnEnabled}
            handleItemMappingChange={handleItemMappingChange}
            usesDateColumns={itemsUsesDateColumns}
            disabled={!isBoardSelected}
          />

          <MappingSection
            title="Sub-items"
            target="subItems"
            columnMapping={columnMapping}
            columns={subItemsColumns}
            getColumnsForField={(types, selectedId) => getColumnsForField(types, selectedId, true)}
            isColumnEnabled={(columnId, types) => isColumnEnabled(columnId, types, true)}
            handleItemMappingChange={handleItemMappingChange}
            usesDateColumns={subItemsUsesDateColumns}
            disabled={!isBoardSelected}
          />

          {/* Global dependency checkbox */}
          <div className={styles.globalDependencyRow}>
            <Checkbox
              checked={dependencyEnabled}
              onChange={handleGlobalDependencyToggle}
              disabled={!hasDependencyColumns || !isBoardSelected}
            />
            <span
              className={classNames(styles.dependencyLabel, {
                [styles.dependencyLabelDisabled]: !hasDependencyColumns || !isBoardSelected,
              })}
            >
              Dependency
            </span>
          </div>

          <div className={styles.createButtonContainer}>
            {/* Step indicator */}
            <div className={styles.stepIndicator}>
              <div className={classNames(styles.stepDot, styles.stepDotActive)} />
              <div className={styles.stepDot} />
            </div>
            <ConfirmButton
              title={itemsLoading ? "Loading items..." : "Next"}
              disabled={isNextDisabled || itemsLoading}
              onClick={handleNext}
              trackingEventName="cim_gantt_chart_next_clicked"
              isLoading={itemsLoading}
            />
          </div>
        </div>
      ) : (
        <GanttChartMappingSkeleton />
      )}
    </div>
  );
}
